// @ts-strict-ignore
import { AfterViewInit, Component, effect, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Subject, fromEvent } from "rxjs";
import { debounceTime, delay, takeUntil } from "rxjs/operators";
import { getEdgeUimiscRequest } from "src/app/shared/jsonrpc/request/getEdgeUimiscRequest";
import { getEdgeUimiscResponse } from "src/app/shared/jsonrpc/response/getEdgeUimiscResponse";
import { Service, Websocket } from "src/app/shared/shared";
import { CurrentData } from "../../../../shared/components/edge/currentdata";
import { ConsumptionSectionComponent } from "./section/consumption.component";
import { GridSectionComponent } from "./section/grid.component";
import { ProductionSectionComponent } from "./section/production.component";
import { StorageSectionComponent } from "./section/storage.component";

@Component({
    selector: "energymonitor-chart",
    templateUrl: "./chart.component.html",
    standalone: false,
})
export class EnergymonitorChartComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild(ConsumptionSectionComponent, { static: true })
    public consumptionSection: ConsumptionSectionComponent;

    @ViewChild(GridSectionComponent, { static: true })
    public gridSection: GridSectionComponent;

    @ViewChild(ProductionSectionComponent, { static: true })
    public productionSection: ProductionSectionComponent;

    @ViewChild(StorageSectionComponent, { static: true })
    public storageSection: StorageSectionComponent;

    @ViewChild("energymonitorChart", { static: true })
    private chartDiv: ElementRef;

    public readonly spinnerId = "energymonitor";
    public translation: string;
    public width: number;
    public height: number;
    public gridMode: number;
    public proLimit: number;

    private ngUnsubscribe: Subject<void> = new Subject<void>();

    constructor(
        private service: Service,
        private websocket: Websocket,
    ) {
        effect(() => {
            const edge = this.service.currentEdge();
            if (edge) {
                const req = new getEdgeUimiscRequest({ edgeId: edge.id });
                this.websocket.sendSafeRequest(req)
                    .then((res) => {
                        this.proLimit = (res as getEdgeUimiscResponse).result.edge[2]?.value;
                    });
            }
        });
    }
    @Input()
    set currentData(currentData: CurrentData) {
        this.service.stopSpinner(this.spinnerId);
        this.updateCurrentData(currentData);
    }
    ngOnInit() {
        this.service.startSpinner(this.spinnerId);
        // make sure chart is redrawn in the beginning and on window resize
        setTimeout(() => this.updateOnWindowResize(), 500);
        const source = fromEvent(window, "resize", null, null);
        source.pipe(takeUntil(this.ngUnsubscribe), debounceTime(200), delay(100)).subscribe(e => {
            this.updateOnWindowResize();
        });
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.updateOnWindowResize();
        });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    /**
   * This method is called on every change of values.
   */
    updateCurrentData(currentData: CurrentData) {
    /*
     * Set values for energy monitor
     */

        const { system, storage, production, grid, consumption } = currentData.summary;

        if (this.proLimit) {
            production.powerRatio = production.activePower / this.proLimit;
        }

        const updatedSummary = { system, storage, production, grid, consumption };

        [this.consumptionSection, this.gridSection, this.productionSection, this.storageSection]
            .filter(section => section != null)
            .forEach(section => {
                section.updateCurrentData(updatedSummary);
            });
    }

    /**
   * This method is called on every change of resolution of the browser window.
   */
    private updateOnWindowResize(): void {
        let size = Math.min(window.innerWidth, window.innerHeight) * 0.75;
        if (this.chartDiv.nativeElement.offsetParent) {
            size = Math.min(size, this.chartDiv.nativeElement.offsetParent.offsetWidth - 30);
        }
        if (size > window.innerHeight) {
            size = window.innerHeight;
        }
        this.height = this.width = size;
        this.translation = `translate(${this.width / 2}, ${this.height / 2})`;
        const outerRadius = Math.min(this.width, this.height) / 2;
        const innerRadius = outerRadius - (outerRadius * 0.1378);
        // All sections from update() in section
        [this.consumptionSection, this.gridSection, this.productionSection, this.storageSection]
            .filter(section => section != null)
            .forEach(section => {
                section.updateOnWindowResize(outerRadius, innerRadius, this.height, this.width);
            });
    }

    private deg2rad(value: number): number {
        return value * (Math.PI / 180);
    }
}
